import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_BASE_URL } from "./Constants";
import { StudentDashboard } from "./StudentDashboard";
import { useNavigate } from "react-router-dom";
import Display from "./Display";
import ViewSessions from "./ViewSessions";
const SessionScheduler = () => {
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    enrollment_id: "",
    scheduled_time: "",
    duration_minutes: 60,
    status: "scheduled",
    meeting_link: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const navigate = useNavigate();

  // Fetch enrollments from API
  useEffect(() => {
    const fetchEnrollments = async () => {
      try {
        const res = await axios.get(
          `${API_BASE_URL}/api/enrollments?with=tutor,student`
        );
        setEnrollments(res.data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching enrollments", err);
        setMessage("Failed to load enrollments");
        setLoading(false);
      }
    };

    fetchEnrollments();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage("");

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/sessions`,
        formData
      );
      setMessage("Session scheduled successfully!");
      // Reset form
      setFormData({
        enrollment_id: "",
        scheduled_time: "",
        duration_minutes: 60,
        status: "scheduled",
        meeting_link: "",
      });
    } catch (error) {
      console.error("Error scheduling session", error);
      setMessage("Failed to schedule session");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <span className="loading loading-spinner loading-lg"></span>
        <span className="ml-2">Loading enrollments...</span>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 max-w-6xl">
      <h1 className="text-3xl font-bold text-center mb-8 text-primary">
        Schedule a New Session
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Form Section */}
        <div className="card bg-base-100 shadow-xl">
          <div className="card-body">
            <h2 className="card-title text-xl mb-4">Session Details</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-semibold">
                    Select Course
                  </span>
                </label>
                <select
                  className="select select-bordered select-md"
                  name="enrollment_id"
                  value={formData.enrollment_id}
                  onChange={handleChange}
                  required
                >
                  <option value="">Choose a course</option>
                  {enrollments.map((enrollment) => (
                    <option key={enrollment.id} value={enrollment.id}>
                      {enrollment.course_name}
                      {enrollment.student &&
                        enrollment.tutor &&
                        ` (${enrollment.student.name} with ${enrollment.tutor.name})`}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">
                      Scheduled Time
                    </span>
                  </label>
                  <input
                    type="datetime-local"
                    className="input input-bordered input-md"
                    name="scheduled_time"
                    value={formData.scheduled_time}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">
                      Duration (minutes)
                    </span>
                  </label>
                  <input
                    type="number"
                    className="input input-bordered input-md"
                    name="duration_minutes"
                    value={formData.duration_minutes}
                    onChange={handleChange}
                    min="15"
                    max="240"
                    step="15"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">Status</span>
                  </label>
                  <select
                    className="select select-bordered select-md"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    required
                  >
                    <option value="scheduled">Scheduled</option>
                  </select>
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">
                      Meeting Link (optional)
                    </span>
                  </label>
                  <input
                    type="url"
                    className="input input-bordered input-md"
                    name="meeting_link"
                    value={formData.meeting_link}
                    onChange={handleChange}
                    placeholder="https://meet.google.com/xxx-xxxx-xxx"
                  />
                </div>
              </div>

              <div className="form-control mt-6">
                <button
                  type="submit"
                  className={`btn btn-primary btn-lg ${
                    submitting ? "loading" : ""
                  }`}
                  disabled={submitting}
                >
                  {submitting ? "Scheduling..." : "Schedule Session"}
                </button>
              </div>

              {message && (
                <div
                  className={`alert ${
                    message.includes("success")
                      ? "alert-success"
                      : "alert-error"
                  } mt-4`}
                >
                  <span>{message}</span>
                </div>
              )}
            </form>
          </div>
        </div>

        {/* Courses Section */}
        <div>
          <h2 className="text-2xl font-bold mb-4 text-secondary">
            Available Courses
          </h2>
          {enrollments.length === 0 ? (
            <div className="alert alert-warning shadow-lg">
              <span>
                No enrollments found. Please create enrollments first.
              </span>
            </div>
          ) : (
            <div className="overflow-x-auto overflow-y-auto rounded-lg shadow h-96">
              <table className="table table-zebra table-auto w-full">
                <thead className="bg-base-200">
                  <tr>
                    <th className="px-4 py-3">ID</th>
                    <th className="px-4 py-3">Course Name</th>
                    <th className="px-4 py-3">Type</th>
                    <th className="px-4 py-3">Subject</th>
                    <th className="px-4 py-3">Student</th>
                    <th className="px-4 py-3">Grade</th>
                    <th className="px-4 py-3">Tutor</th>
                    <th className="px-4 py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {enrollments.map((enrollment) => (
                    <tr key={enrollment.id} className="hover">
                       <td className="px-4 py-3 font-medium">
                        {enrollment.id}
                      </td>
                      <td className="px-4 py-3 font-medium">
                        {enrollment.course_name}
                      </td>
                      <td className="px-4 py-3">
                        <span className="badge badge-outline">
                          {enrollment.type}
                        </span>
                      </td>
                      <td className="px-4 py-3">{enrollment.subject}</td>
                      <td className="px-4 py-3 text-primary font-bold">
                        {enrollment.student
                          ? enrollment.student.name
                          : `ID: ${enrollment.student_id}`}
                      </td>

                      <td className="px-4 py-3">Grade {enrollment.grade}</td>
                      <td className="px-4 py-3 text-success font-bold">
                        {enrollment.tutor
                          ? enrollment.tutor.name
                          : `ID: ${enrollment.tutor_id}`}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`badge ${
                            enrollment.status === "active"
                              ? "badge-success"
                              : "badge-warning"
                          }`}
                        >
                          {enrollment.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      <ViewSessions />
    </div>
  );
};

export default SessionScheduler;
